import { lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "../components/ChunkErrorBoundary";

/**
 * AS PÁGINAS QUE ABREM SEM LOGIN — uma tabela, não cinco `if` iguais.
 *
 * ═══ POR QUE SAÍRAM DO `App.tsx` (30/08) ═══════════════════════════════════
 * Eram CINCO blocos praticamente idênticos, de 8 linhas cada, colados um sob o
 * outro antes dos providers de auth. Somando os `lazy`, 45 linhas de repetição
 * num arquivo que já vive na dívida do portão de tamanho (919 linhas).
 *
 * O portão reprovou o mapa da carteira por 9 linhas, e estava certo: a regra da
 * casa é que **capacidade nova entra por EXTRAÇÃO**. Extrair a repetição foi
 * mais barato e melhor que espremer o código novo.
 *
 * ═══ 🔒 O QUE NÃO PODE MUDAR, E POR QUÊ ════════════════════════════════════
 * Estas páginas são montadas **ANTES** dos providers de sessão. Não é detalhe
 * de organização: quem abre `/p/<token>` é o cliente, que NÃO tem sessão. Pôr
 * qualquer uma delas dentro do `AuthProvider` a mandaria pro login e quebraria
 * o aceite de proposta, a aprovação de medição e o selo público de uma vez.
 *
 * Por isso a decisão continua sendo tomada por `window.location.pathname`, e
 * não pelo `<Routes>`: o roteador do React só existe depois dos providers.
 */

const PropostaPublicaPage = lazy(() => import("../pages/PropostaPublica").then((m) => ({ default: m.PropostaPublicaPage })));
const RelatorioPublicoPage = lazy(() => import("../pages/RelatorioPublico").then((m) => ({ default: m.RelatorioPublicoPage })));
const MedicaoPublicaPage = lazy(() => import("../pages/MedicaoPublica").then((m) => ({ default: m.MedicaoPublicaPage })));
const ConfirmacaoPublicaPage = lazy(() => import("../pages/ConfirmacaoPublica").then((m) => ({ default: m.ConfirmacaoPublicaPage })));
const SeloPublicoPage = lazy(() => import("../pages/SeloPublico").then((m) => ({ default: m.SeloPublicoPage })));
const PassaportePublicoPage = lazy(() => import("../pages/PassaportePublico").then((m) => ({ default: m.PassaportePublicoPage })));
const EquipamentoPublicoPage = lazy(() => import("../pages/EquipamentoPublico").then((m) => ({ default: m.EquipamentoPublicoPage })));
const RespostaSemLoginPage = lazy(() => import("../pages/RespostaSemLogin").then((m) => ({ default: m.RespostaSemLoginPage })));

/** Cada prefixo, quem abre e para quê — o comentário morava em cada `if`. */
export const PAGINAS_PUBLICAS = [
  { prefixo: "/p/", Pagina: PropostaPublicaPage, quem: "o cliente aceita a proposta" },
  { prefixo: "/r/", Pagina: RelatorioPublicoPage, quem: "o contratante abre o relatório de obra congelado" },
  { prefixo: "/medicao/", Pagina: MedicaoPublicaPage, quem: "o cliente aprova a medição" },
  { prefixo: "/confirmar/", Pagina: ConfirmacaoPublicaPage, quem: "o aprovador com alçada confirma o achado" },
  { prefixo: "/selo/", Pagina: SeloPublicoPage, quem: "o síndico mostra o selo de conformidade" },
  // D14: /passaporte/ e não /p/ (que já é a proposta) — o QR da portaria abre o selo aberto documento por documento
  { prefixo: "/passaporte/", Pagina: PassaportePublicoPage, quem: "quem escaneia o QR na portaria vê a validade dos documentos" },
  // mock 45 seção 3 (14/10): o link sem login que o operador manda pelo WhatsApp — a ponta responde UMA pendência sem senha
  { prefixo: "/responder/", Pagina: RespostaSemLoginPage, quem: "quem recebeu o link pelo WhatsApp responde uma pendência" },
  // mercado ideia 6 (19/10): o QR colado no equipamento. Endereço PRÓPRIO, fora do /p/ da proposta (auditoria 3 rodada 4,
  // item 2): o antigo /p/eq/ só responde 301 para cá, em functions/p/[[token]].ts, até 17/01/2027.
  { prefixo: "/eq/", Pagina: EquipamentoPublicoPage, quem: "quem escaneia o QR do equipamento vê o estado dele e o passaporte da unidade" },
] as const;

function Carregando() {
  return <div className="p-8 text-muted-foreground">Carregando…</div>;
}

/**
 * A página pública desta URL, ou `null` quando é rota do painel.
 *
 * ⚠️ Casa por PREFIXO, e a ordem da tabela não importa porque os prefixos são
 * distintos entre si — nenhum é prefixo de outro. Se um dia alguém acrescentar
 * `/p/relatorio/`, isto precisa virar casamento por rota, não por prefixo.
 */
export function paginaPublicaDaUrl(pathname: string) {
  const alvo = PAGINAS_PUBLICAS.find((p) => pathname.startsWith(p.prefixo));
  if (!alvo) return null;
  const { Pagina } = alvo;
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<Carregando />}>
        <Pagina />
      </Suspense>
    </ChunkErrorBoundary>
  );
}
